import React, { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { MessageSquare, Bug, Lightbulb, HelpCircle, Mail, CheckCircle, Eye, Wrench, X, Send, AlertCircle, Search, RefreshCw, Save, Clock, User } from 'lucide-react'

import { C } from './theme'

const ESTADOS = [
  { val: 'recebido',           label: 'Recebido',        emoji: '🆕', color: C.textMuted, bg: '#f3f2f1' },
  { val: 'em_analise',         label: 'Em análise',      emoji: '👁️', color: C.primary,   bg: '#eff6fc' },
  { val: 'em_implementacao',   label: 'Em implementação', emoji: '🔧', color: C.warning,   bg: '#fff4ce' },
  { val: 'concluido',          label: 'Concluído',       emoji: '✅', color: C.success,   bg: '#dff6dd' },
  { val: 'nao_aplicavel',      label: 'Não aplicável',   emoji: '❌', color: C.danger,    bg: '#fde7e9' },
]

const TIPOS = {
  sugestao: { label: 'Sugestão', emoji: '💡', Icon: Lightbulb,    color: C.success },
  bug:      { label: 'Bug',      emoji: '🐛', Icon: Bug,           color: C.danger  },
  duvida:   { label: 'Dúvida',   emoji: '❓', Icon: HelpCircle,    color: C.primary },
  outro:    { label: 'Outro',    emoji: '✉️', Icon: Mail,          color: C.textMuted },
}

function getEstadoCfg(estado) {
  return ESTADOS.find(e => e.val === estado) || ESTADOS[0]
}

export default function Feedback() {
  const { isAdmin } = useAuth()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('pendentes') // 'todos' | 'pendentes' | estado específico
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [search, setSearch] = useState('')
  const [editando, setEditando] = useState(null) // feedback aberto para edição

  useEffect(() => { loadFeedback() }, [])

  const loadFeedback = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('feedback')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) console.error('Erro feedback:', error.message)
    setItems(data || [])
    setLoading(false)
  }

  const guardar = async () => {
    if (!editando) return
    const updates = {
      estado: editando.estado,
      resposta_publica: (editando.resposta_publica || '').trim() || null,
      notas_admin: (editando.notas_admin || '').trim() || null,
      estado_atualizado_em: new Date().toISOString(),
      resolvido: editando.estado === 'concluido',
      resolved_at: editando.estado === 'concluido' ? new Date().toISOString() : null,
    }
    const { error } = await supabase.from('feedback').update(updates).eq('id', editando.id)
    if (error) { alert('Erro ao guardar: ' + error.message); return }
    setEditando(null)
    await loadFeedback()
  }

  if (!isAdmin) {
    return <div style={{ padding: '20px', textAlign: 'center', color: C.textMuted }}>Acesso reservado.</div>
  }

  // Aplicar filtros
  let lista = items
  if (filtroEstado === 'pendentes') {
    lista = lista.filter(i => i.estado === 'recebido' || i.estado === 'em_analise' || i.estado === 'em_implementacao')
  } else if (filtroEstado !== 'todos') {
    lista = lista.filter(i => i.estado === filtroEstado)
  }
  if (filtroTipo !== 'todos') {
    lista = lista.filter(i => i.tipo === filtroTipo)
  }
  if (search.trim()) {
    const q = search.trim().toLowerCase()
    lista = lista.filter(i =>
      (i.titulo || '').toLowerCase().includes(q) ||
      (i.descricao || '').toLowerCase().includes(q) ||
      (i.user_email || '').toLowerCase().includes(q) ||
      (i.user_nome || '').toLowerCase().includes(q)
    )
  }

  const contadores = {
    todos: items.length,
    pendentes: items.filter(i => i.estado === 'recebido' || i.estado === 'em_analise' || i.estado === 'em_implementacao').length,
    recebido: items.filter(i => i.estado === 'recebido').length,
    em_analise: items.filter(i => i.estado === 'em_analise').length,
    em_implementacao: items.filter(i => i.estado === 'em_implementacao').length,
    concluido: items.filter(i => i.estado === 'concluido').length,
    nao_aplicavel: items.filter(i => i.estado === 'nao_aplicavel').length,
  }

  return (
    // O <main> tem overflow:hidden — a página tem de trazer o seu próprio scroll
    <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto', flex: 1, overflowY: 'auto' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MessageSquare size={20} color={C.primary} /> Feedback dos Clientes
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: C.textMuted }}>
            {contadores.pendentes} por tratar · {contadores.todos} total
          </p>
        </div>
        <button onClick={loadFeedback}
          style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textMuted }}>
          <RefreshCw size={13} /> Atualizar
        </button>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {[
          { val: 'pendentes', label: `Pendentes (${contadores.pendentes})`, recomendado: true },
          { val: 'todos',     label: `Todos (${contadores.todos})` },
          ...ESTADOS.map(e => ({ val: e.val, label: `${e.emoji} ${e.label} (${contadores[e.val] || 0})` })),
        ].map(opt => (
          <button key={opt.val} onClick={() => setFiltroEstado(opt.val)}
            style={{
              height: '30px', padding: '0 12px',
              border: `1px solid ${filtroEstado === opt.val ? C.primary : C.border}`,
              borderRadius: '15px',
              background: filtroEstado === opt.val ? C.primary : C.surface,
              color: filtroEstado === opt.val ? '#fff' : C.text,
              fontSize: '12px', fontWeight: '600', cursor: 'pointer',
            }}>
            {opt.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
          style={{ height: '32px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '12px', background: C.surface }}>
          <option value="todos">Todos os tipos</option>
          {Object.entries(TIPOS).map(([val, t]) => (
            <option key={val} value={val}>{t.emoji} {t.label}</option>
          ))}
        </select>
        <div style={{ position: 'relative', flex: 1, maxWidth: '320px' }}>
          <Search size={13} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Procurar título, descrição, cliente..."
            style={{ width: '100%', height: '32px', padding: '0 10px 0 30px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box', outline: 'none' }} />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted }}>A carregar...</div>
      ) : lista.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: C.textMuted, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px' }}>
          Sem feedbacks que correspondam aos filtros.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {lista.map(item => {
            const tipo = TIPOS[item.tipo] || TIPOS.outro
            const estCfg = getEstadoCfg(item.estado)
            return (
              <div key={item.id} onClick={() => setEditando({ ...item })}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '12px 16px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={ev => ev.currentTarget.style.borderColor = C.primary}
                onMouseLeave={ev => ev.currentTarget.style.borderColor = C.border}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: `${tipo.color}15`, color: tipo.color }}>
                        {tipo.emoji} {tipo.label}
                      </span>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: estCfg.bg, color: estCfg.color }}>
                        {estCfg.emoji} {estCfg.label}
                      </span>
                    </div>
                    <h3 style={{ margin: '4px 0 4px', fontSize: '14px', fontWeight: '700', color: C.text }}>{item.titulo}</h3>
                    <p style={{ margin: 0, fontSize: '12px', color: C.textMuted, lineHeight: '1.5', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {item.descricao}
                    </p>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '11px', color: C.textMuted, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <User size={11} /> {item.user_nome || item.user_email || 'Anónimo'}
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={11} /> {new Date(item.created_at).toLocaleDateString('pt-PT')} {new Date(item.created_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal de edição */}
      {editando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setEditando(null) }}>
          <div style={{ background: C.surface, borderRadius: '8px', width: '100%', maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ padding: '16px 20px', background: C.primary, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px 8px 0 0' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Gerir feedback</h2>
              <button onClick={() => setEditando(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '6px', borderRadius: '4px', cursor: 'pointer', display: 'flex' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Info original */}
              <div style={{ padding: '12px', background: C.bg, borderRadius: '4px', border: `1px solid ${C.border}` }}>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: `${(TIPOS[editando.tipo] || TIPOS.outro).color}15`, color: (TIPOS[editando.tipo] || TIPOS.outro).color }}>
                    {(TIPOS[editando.tipo] || TIPOS.outro).emoji} {(TIPOS[editando.tipo] || TIPOS.outro).label}
                  </span>
                </div>
                <h3 style={{ margin: '4px 0 6px', fontSize: '14px', fontWeight: '700', color: C.text }}>{editando.titulo}</h3>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: C.text, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{editando.descricao}</p>
                <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: C.textMuted, flexWrap: 'wrap' }}>
                  <span><User size={10} style={{ display: 'inline-block', verticalAlign: '-1px' }} /> {editando.user_nome || editando.user_email}</span>
                  <span><Clock size={10} style={{ display: 'inline-block', verticalAlign: '-1px' }} /> {new Date(editando.created_at).toLocaleString('pt-PT')}</span>
                </div>
              </div>

              {/* Estado */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase' }}>Estado (visível ao cliente)</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {ESTADOS.map(e => (
                    <button key={e.val} type="button" onClick={() => setEditando(p => ({ ...p, estado: e.val }))}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${editando.estado === e.val ? e.color : C.border}`,
                        borderRadius: '4px',
                        background: editando.estado === e.val ? e.bg : C.surface,
                        color: editando.estado === e.val ? e.color : C.text,
                        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                      }}>
                      {e.emoji} {e.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resposta pública */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase' }}>Resposta pública (visível ao cliente)</label>
                <textarea value={editando.resposta_publica || ''}
                  onChange={e => setEditando(p => ({ ...p, resposta_publica: e.target.value }))}
                  placeholder="Ex: Vamos analisar e dar resposta em breve."
                  rows={3}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', color: C.text, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>

              {/* Notas admin */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, marginBottom: '6px', textTransform: 'uppercase' }}>Notas internas (só admin)</label>
                <textarea value={editando.notas_admin || ''}
                  onChange={e => setEditando(p => ({ ...p, notas_admin: e.target.value }))}
                  placeholder="Notas para a equipa (não visível ao cliente)..."
                  rows={2}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', color: C.text, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
            </div>
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setEditando(null)}
                style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.textMuted, fontSize: '12px', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={guardar}
                style={{ height: '34px', padding: '0 16px', background: C.primary, color: '#fff', border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Save size={13} /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
